import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { formValueSelector } from 'redux-form';
import { PlaidLink } from 'react-plaid-link';
import { Link } from 'react-router-dom';
import { ModalManager, Effect } from '../../react-dynamic-modal';
import { BasicModal, AccountBody } from '@components/_shared/Modals/Modals';
import { FormPageTopPart } from '../../_shared/FormPageTopPart/FormPageTopPart';
import { ELEMENTS_GAP } from '@utils/spacing';
import { cn } from '@utils/cn';
import { Helmet } from 'react-helmet';
import { Spinner } from '@components/Spinner/Spinner';
import { plaidConnect } from '../../../api/plaidConnect';
import { DataError } from '../../../api/DataError';
import { Alert2 as Alert, Conduit, LoadingDots } from 'shared-components';
import { AlertTitle2 as AlertTitle } from 'shared-components';
import { useDispatch } from 'react-redux';
import { change } from 'redux-form';
import { plaidLinkToken } from '../../../api/plaidLinkToken';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { Button, Loader, Stack, StepCard, useResponsiveBreakpoints } from 'shared-components';
import { SkipConnectCard } from './SkipConnectCard';
import { isSyntheticTest } from '@utils/platformBasedInfo';
import { recordApplyEvent } from '@utils/recordEvent';

const PlaidConnectCard = ({
  loan_id,
  loan_amount,
  first,
  setTemp,
  business_id,
  customConnectEndpoint,
  customLinkTokenEndpoint,
  customOnConnect,
  match,
  dataLayerPush,
  nextRoute,
  statementsType,
  loanType,
  textConnectWithPlaidTextBody = 'Securely connect your bank account with our trusted partner Plaid.',
  textConnectWithPlaidTextSuccessBody = '',
  plaidAboutToConnectStepCardProps = {},
  allowSkipConnect = true,
  section,
}) => {
  const loanTypeSafe = Array.isArray(loanType)
    ? loanType.filter((lt) => lt !== 'UNKNOWN').length > 0
      ? loanType
          .filter((lt) => lt !== 'UNKNOWN')[0]
          .toString()
          .toUpperCase()
      : 'UNKNOWN'
    : loanType?.toString().toUpperCase() ?? 'UNKNOWN';
  const responsiveBreakpoints = useResponsiveBreakpoints();
  const history = useHistory();
  const [{ data: plaidConnectResponse, loading: plaidConnectLoading, error: plaidConnectError }, triggerPlaidConnect] =
    plaidConnect();
  const [
    { data: plaidLinkTokenResponse, loading: plaidLinkTokenLoading, error: plaidLinkTokenError },
    generatePlaidLinkToken,
  ] = plaidLinkToken();

  const dispatch = useDispatch();
  const [error, setError] = useState(null);
  const [token, setToken] = useState(null);
  const [metadata, setMetadata] = useState();
  const isPlaidAlreadyConnected = statementsType === 'plaid';

  useEffect(() => {
    if (plaidConnectResponse) {
      // /tools/plaid/connect streams a keep-alive HTTP 200 even when the bank
      // linked but Plaid could not build the report (DATA_UNAVAILABLE, still
      // pending) — failure travels in the body as { error: true, message }.
      // Never mark statements as provided on that path: the customer must
      // retry or upload statements instead.
      if (plaidConnectResponse.error) {
        ModalManager.close();
        setError(
          plaidConnectResponse.message ||
            'We could not retrieve your bank data. Please try again or choose to upload statements manually.'
        );
        return;
      }
      setTemp({ haveStatements: true });
      dispatch(change('application', 'have_statements', true));
      dispatch(change('application', 'type_of_statements', 'plaid'));

      ModalManager.close();

      if (customOnConnect) {
        customOnConnect(plaidConnectResponse);
      } else {
        nextRoute();
      }
    }
  }, [plaidConnectResponse]);

  useEffect(() => {
    if (plaidConnectError) {
      ModalManager.close();
    }
  }, [plaidConnectError]);

  const connectBank = async (metadata, mainAccountId, publicToken) => {
    // showProcessingModal(
    //   'We are processing your application',
    //   'This step can take several minutes, please do not click back on your internet browser or refresh this page.'
    // );
    console.log('[PLAID] connectBank', metadata, mainAccountId, publicToken);

    triggerPlaidConnect({
      data: {
        loan_id: loan_id,
        loan_type: loanTypeSafe,
        amount: loan_amount,
        first: first,
        bank_accounts: metadata,
        main_account_id: mainAccountId,
        public_token: publicToken,
      },
      ...(customConnectEndpoint ? { url: customConnectEndpoint } : {}),
    });
  };

  useEffect(() => {
    generatePlaidLinkToken({
      data: {
        accountId: business_id,
      },
      ...(customLinkTokenEndpoint ? { url: customLinkTokenEndpoint } : {}),
    });
  }, []);

  useEffect(() => {
    if (plaidLinkTokenError) {
      console.error('Error generating plaid link token', plaidLinkTokenError);
    }
  }, [plaidLinkTokenError]);

  useEffect(() => {
    if (plaidLinkTokenResponse && plaidLinkTokenResponse.link_token) {
      setToken(plaidLinkTokenResponse.link_token);
    }
  }, [plaidLinkTokenResponse]);

  const handleExit = (error, metadata) => {
    console.log('[PLAID] handleExit', error, metadata);
    if (error !== null) {
      setError('Error connecting to bank account. Please try again or choose to upload statements manually.');
    }
    if (metadata?.accounts && metadata.accounts.length === 0) {
      setError('No bank accounts connected. Please make sure to give access to at least one checking account.');
    }
  };

  const handleEvent = (event, metadata) => {
    console.log('[PLAID] handleEvent', event, metadata);
    if (event === 'OPEN' || event === 'open') {
      if (!process.env.STORYBOOK_TEST && !isSyntheticTest) {
        analytics.track('Plaid Connect Clicked', {
          section,
          subtype: 'Plaid Connect',
        });
        recordApplyEvent('Plaid Connect Clicked', {
          section,
          subtype: 'Plaid Connect',
        });
      }
    }
    if (event === 'ERROR' || event === 'error') {
      setError('Error connecting to bank account. Please try again.');
    }
  };

  const openAccountSelection = (accounts, metadata, token) => {
    const title = 'Please select your primary business checking account:';
    const body = (
      <AccountBody
        accounts={accounts}
        itemClick={(mainAccountId) => {
          ModalManager.close();
          if (!process.env.STORYBOOK_TEST && !isSyntheticTest) {
            analytics.track('Plaid Connected Successfully', {
              section,
              subtype: 'Plaid Connect',
            });
            recordApplyEvent('Plaid Connected Successfully', {
              section,
              subtype: 'Plaid Connect',
            });
          }
          connectBank(metadata, mainAccountId, token);
        }}
      />
    );
    ModalManager.open(
      <BasicModal effect={Effect.RotateFromBottom3D} title={title} body={body} onRequestClose={() => false} />
    );
  };

  // For testing purposes:
  // openAccountSelection([
  //   {
  //     id: '1',
  //     name: 'Test Account',
  //     mask: '1234',
  //   },
  //   {
  //     id: '2',
  //     name: 'Test Account 2',
  //     mask: '5678',
  //   },
  // ]);
  const handleSuccess = (token, metadata) => {
    setMetadata(metadata);
    setToken(token);
    console.log('[PLAID] handleSuccess', token, metadata);
    if (metadata.accounts) {
      console.log('[PLAID] found accounts', metadata.accounts);
      let checkingAccounts = metadata.accounts.filter((account) => {
        return account.subtype === 'checking';
      });

      if (checkingAccounts.length === 0) {
        console.log('[PLAID] no checking accounts ', checkingAccounts);
        setError('No checking accounts found. Please connect bank account with a checking account.');
      } else {
        console.log('[PLAID] checking accounts are:', checkingAccounts);
        if (checkingAccounts.length === 1) {
          if (!process.env.STORYBOOK_TEST && !isSyntheticTest) {
            analytics.track('Plaid Connected Successfully', {
              section,
              subtype: 'Plaid Connect',
            });
            recordApplyEvent('Plaid Connected Successfully', {
              section,
              subtype: 'Plaid Connect',
            });
          }
          connectBank(metadata, checkingAccounts[0].id, token);
        } else {
          openAccountSelection(checkingAccounts, metadata, token);
        }
      }
    } else {
      console.error('[PLAID] No accounts found. ', metadata.accounts);
      setError('No accounts found. Please make sure to give access to at least one checking account.');
    }
  };
  if (!token && !plaidLinkTokenError) {
    return (
      <StepCard
        type="current"
        headline={undefined}
        textBody={<Loader />}
        cardProps={{ className: 'w-full w-[280px] justify-between' }}
        buttonProps={{
          children: '',
        }}
        button={undefined}
      />
    );
  }
  return (
    <>
      {isPlaidAlreadyConnected && (
        <StepCard
          additionalComponentAboveHeadline={
            <svg width="50" height="51" viewBox="0 0 50 51" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect y="0.300049" width="50" height="50" rx="25" fill="#F2EAE4" />
              <path
                d="M35.7392 34.1836L31.804 30.2485L34.897 27.1558L37.0007 29.2596L35.7392 34.1836ZM34.897 23.4443L31.804 20.3516L35.7392 16.4166L37.0007 21.3408L34.897 23.4443ZM28.9597 13.2993L33.8837 14.5607L29.9486 18.4959L26.8558 15.4032L28.9597 13.2993ZM23.1445 15.4033L20.0516 18.4959L16.1166 14.5608L21.0406 13.2993L23.1445 15.4033ZM14.2606 16.4162L18.196 20.3517L15.1033 23.4443L12.9991 21.3401L14.2606 16.4162ZM15.1033 27.1556L18.196 30.2482L14.2606 34.1836L12.9991 29.2597L15.1033 27.1556ZM21.0404 37.3008L16.1163 36.0393L20.0516 32.104L23.1444 35.1968L21.0404 37.3008ZM28.0928 30.2484L25 33.3412L21.9072 30.2484L25 27.1556L28.0928 30.2484ZM29.9485 22.2072L33.0412 25.3L29.9485 28.3928L26.8556 25.3L29.9485 22.2072ZM23.1444 25.3L20.0516 28.3928L16.959 25.3L20.0516 22.2073L23.1444 25.3ZM21.9074 20.3517L25.0001 17.2589L28.0928 20.3516L25 23.4443L21.9074 20.3517ZM26.8556 35.1968L29.9485 32.1042L33.8835 36.0393L28.9595 37.3006L26.8556 35.1968ZM37.603 37.903L40 28.5474L36.7526 25.3001L39.9997 22.0525L37.6029 12.6969L28.2472 10.3L25.0001 13.5475L21.7524 10.3001L12.3968 12.6971L10 22.0527L13.2476 25.3L10 28.5476L12.3968 37.9032L21.7526 40.3001L25 37.0524L28.2473 40.2999L37.603 37.903Z"
                fill="#0D0D0D"
              />
            </svg>
          }
          type="completed"
          headline="Plaid is Now Connected"
          textBody={
            textConnectWithPlaidTextSuccessBody ?? 'Securely connect your bank account with our trusted partner Plaid.'
          }
          button="Connected"
          buttonProps={{
            children: 'Continue',
            variant: 'cta-active',
            className: 'bg-sparkHover !leading-[25px] !text-[18px] !font-condensed !h-[60px]',
            disabled: true,

            onClick: () => {
              if (customOnConnect) {
                customOnConnect(plaidConnectResponse);
              } else {
                nextRoute();
              }
            },
          }}
          cardProps={{
            className: 'justify-between text-dawn !py-[40px]',
          }}
          headlineProps={{
            className: '!text-dawn',
          }}
          textBodyProps={{
            className: '!text-dawn',
          }}
        />
      )}
      {token && !isPlaidAlreadyConnected && (
        <StepCard
          type="upcomming"
          headline="Connect with Plaid"
          textBody={
            <>
              {textConnectWithPlaidTextBody}
              {(plaidConnectError || plaidLinkTokenError || plaidLinkTokenError || error) && (
                <Stack gap={ELEMENTS_GAP}>
                  {plaidConnectError && (
                    <Alert severity="error">
                      <AlertTitle>Failed to connect</AlertTitle>
                      <DataError msg="Unfortunately, connecting bank account failed." error={plaidConnectError} />
                    </Alert>
                  )}
                  {error && <div style={{ color: '#ed4337' }}>{error}</div>}
                  <div className="plaid-button-wrap">
                    {plaidLinkTokenError && (
                      <Alert severity="error">
                        <AlertTitle>Connect disabled</AlertTitle>
                        <DataError
                          msg="Unfortunately, connecting bank account is now disabled. Please choose to upload statements manually. Sorry for the inconvenience."
                          error={plaidLinkTokenError}
                        />
                      </Alert>
                    )}
                  </div>
                </Stack>
              )}
            </>
          }
          additionalComponent={
            <>
              {plaidConnectLoading ? (
                <Button
                  variant="cta-form"
                  color="primary"
                  className="!text-charcoal !text-center !uppercase !bg-dawn !font-condensed !text-[18px] !px-[20px] !leading-normal !h-[50px] !btn-base !bg-spark !text-charcoal !hover:bg-sparkHover !border-charcoal !rounded-[100px] !border !btn-primary-green bg-sparkHover !leading-[25px] !text-[18px] !font-condensed !h-[60px] !w-full"
                  type="submit"
                  disabled={true}
                  isLoading={true}
                >
                  Connecting
                </Button>
              ) : (
                <PlaidLink
                  token={token}
                  onExit={handleExit}
                  onEvent={handleEvent}
                  onSuccess={handleSuccess}
                  className={cn(
                    '!text-charcoal !text-center !uppercase !bg-dawn !font-condensed !text-[18px] !px-[20px] !leading-normal !h-[50px] !btn-base !bg-spark !text-charcoal !hover:bg-sparkHover !border-charcoal !rounded-[100px] !border !btn-primary-green bg-sparkHover !leading-[25px] !text-[18px] !font-condensed !h-[60px] !w-full',
                    plaidConnectLoading ? '!disabled' : ''
                  )}
                  disabled={plaidConnectLoading}
                >
                  Connect
                </PlaidLink>
              )}
              {allowSkipConnect && responsiveBreakpoints.isMobile && (
                <SkipConnectCard
                  asText
                  match={match}
                  loan_id={loan_id}
                  first={first}
                  dataLayerPush={dataLayerPush}
                  history={history}
                  nextRoute={nextRoute}
                />
              )}
            </>
          }
          cardProps={{ className: 'w-full justify-between !py-[40px]' }}
          additionalComponentAboveHeadline={
            <svg width="50" height="51" viewBox="0 0 50 51" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect y="0.300049" width="50" height="50" rx="25" fill="#0D0D0D" />
              <path
                d="M35.7392 34.1836L31.804 30.2485L34.897 27.1558L37.0007 29.2596L35.7392 34.1836ZM34.897 23.4443L31.804 20.3516L35.7392 16.4166L37.0007 21.3408L34.897 23.4443ZM28.9597 13.2993L33.8837 14.5607L29.9486 18.4959L26.8558 15.4032L28.9597 13.2993ZM23.1445 15.4033L20.0516 18.4959L16.1166 14.5608L21.0406 13.2993L23.1445 15.4033ZM14.2606 16.4162L18.196 20.3517L15.1033 23.4443L12.9991 21.3401L14.2606 16.4162ZM15.1033 27.1556L18.196 30.2482L14.2606 34.1836L12.9991 29.2597L15.1033 27.1556ZM21.0404 37.3008L16.1163 36.0393L20.0516 32.104L23.1444 35.1968L21.0404 37.3008ZM28.0928 30.2484L25 33.3412L21.9072 30.2484L25 27.1556L28.0928 30.2484ZM29.9485 22.2072L33.0412 25.3L29.9485 28.3928L26.8556 25.3L29.9485 22.2072ZM23.1444 25.3L20.0516 28.3928L16.959 25.3L20.0516 22.2073L23.1444 25.3ZM21.9074 20.3517L25.0001 17.2589L28.0928 20.3516L25 23.4443L21.9074 20.3517ZM26.8556 35.1968L29.9485 32.1042L33.8835 36.0393L28.9595 37.3006L26.8556 35.1968ZM37.603 37.903L40 28.5474L36.7526 25.3001L39.9997 22.0525L37.6029 12.6969L28.2472 10.3L25.0001 13.5475L21.7524 10.3001L12.3968 12.6971L10 22.0527L13.2476 25.3L10 28.5476L12.3968 37.9032L21.7526 40.3001L25 37.0524L28.2473 40.2999L37.603 37.903Z"
                fill="#F2EAE4"
              />
            </svg>
          }
          buttonContainerProps={{
            className: 'w-full flex-1 justify-center',
          }}
          {...plaidAboutToConnectStepCardProps}
        />
      )}
    </>
  );
};

const selector = formValueSelector('application');

const mapStateToProps = (state) => {
  return {
    business_id: selector(state, 'business_id'),
    contact_id: selector(state, 'owner_1_id'),
    mobile: selector(state, 'owner_1_mobile'),
    email: selector(state, 'owner_1_email'),
    loanType: selector(state, 'loan_type'),
    statementsType: selector(state, 'type_of_statements'),
    theme: state.theme,
  };
};

export const PlaidConnectCardContainer = connect(mapStateToProps)(PlaidConnectCard);
